import type { ReactNode } from "react";
import { cn } from "../../lib/utils";
import { Label } from "./label";

/**
 * Labeled form-field wrapper. Replaces the repeated
 * `<label><span className="text-xs text-muted-foreground">…</span><Input/></label>`
 * blocks across the
 * Guide/Chapter panels. Pass the control (Input/Textarea/Select) as children.
 *
 * Renders a `<label>` when `htmlFor` is omitted (wrapping the control) or a
 * `<div>` + associated `<label htmlFor>` when an id is provided.
 */
export function Field({
  label,
  hint,
  htmlFor,
  className,
  children,
}: {
  label: ReactNode;
  hint?: ReactNode;
  htmlFor?: string;
  className?: string;
  children: ReactNode;
}) {
  const labelNode = (
    <span className="text-xs font-medium text-muted-foreground">{label}</span>
  );
  const hintNode = hint ? (
    <span className="text-[0.7rem] leading-snug text-muted-foreground/80">{hint}</span>
  ) : null;

  if (htmlFor) {
    return (
      <div className={cn("flex flex-col gap-1", className)}>
        <Label htmlFor={htmlFor}>{labelNode}</Label>
        {children}
        {hintNode}
      </div>
    );
  }

  return (
    <Label className={cn("flex flex-col items-stretch gap-1", className)}>
      {labelNode}
      {children}
      {hintNode}
    </Label>
  );
}
